import { supabase } from './supabaseClient';

export interface MarketData {
  id: string;
  product_name: string;
  category: string;
  location: string;
  price: number;
  unit: string;
  quantity: number;
  quality: 'premium' | 'standard' | 'basic';
  source: 'marketplace' | 'external' | 'government' | 'mock' | 'news' | 'ncpb';
  date: string;
  trend: 'up' | 'down' | 'stable';
  demand_level: 'high' | 'medium' | 'low';
  supply_level: 'high' | 'medium' | 'low';
}

export interface MarketTrend {
  product_name: string;
  category: string;
  location: string;
  current_price: number;
  previous_price: number;
  price_change: number;
  price_change_percentage: number;
  trend_direction: 'up' | 'down' | 'stable';
  demand_trend: 'increasing' | 'decreasing' | 'stable';
  supply_trend: 'increasing' | 'decreasing' | 'stable';
  forecast: {
    next_week: number;
    next_month: number;
    confidence: number;
  };
}

export interface MarketComparison {
  product_name: string;
  category: string;
  locations: {
    location: string;
    price: number;
    quantity: number;
    demand_level: string;
    supply_level: string;
  }[];
  best_price_location: string;
  best_price: number;
  price_variance: number;
  recommendation: string;
}

export interface DemandForecast {
  product_name: string;
  category: string;
  location: string;
  current_demand: number;
  forecasted_demand: {
    next_week: number;
    next_month: number;
    next_quarter: number;
  };
  factors: string[];
  confidence: number;
}

export class MarketIntelligenceService {
  // Get market data for a specific product and location
  static async getMarketData(
    productName?: string,
    location?: string,
    category?: string,
    limit: number = 50
  ): Promise<MarketData[]> {
    try {
      let query = supabase
        .from('market_data')
        .select('*')
        .order('date', { ascending: false });

      if (productName) {
        query = query.ilike('product_name', `%${productName}%`);
      }

      if (location) {
        query = query.eq('location', location);
      }

      if (category) {
        query = query.eq('category', category);
      }

      const { data, error } = await query.limit(limit);

      if (error) {
        console.error('Error fetching market data:', error);
        return [];
      }

      if (!data || data.length === 0) {
        return [];
      }

      return data;
    } catch (error) {
      console.error('Error fetching market data:', error);
      return [];
    }
  }

  // Get market trends for products
  static async getMarketTrends(
    productName?: string,
    location?: string,
    category?: string
  ): Promise<MarketTrend[]> {
    try {
      let query = supabase
        .from('market_data')
        .select('product_name, category, location, price, date, demand_level, supply_level')
        .order('date', { ascending: false })
        .limit(250);

      if (productName) {
        query = query.ilike('product_name', `%${productName}%`);
      }

      if (location) {
        query = query.eq('location', location);
      }

      if (category) {
        query = query.eq('category', category);
      }

      const { data, error } = await query;
      if (error || !data || data.length === 0) return [];

      const groups = new Map<string, any[]>();
      for (const row of data) {
        const key = `${row.product_name}__${row.category}__${row.location}`;
        const arr = groups.get(key);
        if (arr) arr.push(row);
        else groups.set(key, [row]);
      }

      const trends: MarketTrend[] = [];

      for (const [key, rows] of groups.entries()) {
        rows.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        const current = rows[0];
        const previous = rows[1] || rows[0];

        const currentPrice = Number(current.price) || 0;
        const previousPrice = Number(previous.price) || currentPrice;
        const change = currentPrice - previousPrice;
        const changePct = previousPrice === 0 ? 0 : (change / previousPrice) * 100;

        const direction: 'up' | 'down' | 'stable' =
          change > 0 ? 'up' : change < 0 ? 'down' : 'stable';

        const confidence = Math.min(0.9, 0.5 + Math.min(rows.length, 10) * 0.04);

        const nextWeek = Math.max(0, Math.round(currentPrice * (1 + changePct / 100 / 4)));
        const nextMonth = Math.max(0, Math.round(currentPrice * (1 + changePct / 100)));

        const demandTrend: 'increasing' | 'decreasing' | 'stable' =
          current.demand_level === 'high' ? 'increasing' : current.demand_level === 'low' ? 'decreasing' : 'stable';
        const supplyTrend: 'increasing' | 'decreasing' | 'stable' =
          current.supply_level === 'high' ? 'increasing' : current.supply_level === 'low' ? 'decreasing' : 'stable';

        const [p, c, l] = key.split('__');
        trends.push({
          product_name: p,
          category: c,
          location: l,
          current_price: currentPrice,
          previous_price: previousPrice,
          price_change: change,
          price_change_percentage: Number(changePct.toFixed(2)),
          trend_direction: direction,
          demand_trend: demandTrend,
          supply_trend: supplyTrend,
          forecast: { next_week: nextWeek, next_month: nextMonth, confidence }
        });
      }

      return trends;
    } catch (error) {
      console.error('Error fetching market trends:', error);
      return [];
    }
  }

  // Compare prices across different markets
  static async getMarketComparison(
    productName: string,
    category?: string
  ): Promise<MarketComparison[]> {
    try {
      const marketData = await this.getMarketData(productName);
      
      // Group by product and category
      const groupedData = marketData.reduce((acc, item) => {
        const key = `${item.product_name}-${item.category}`;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(item);
        return acc;
      }, {} as Record<string, MarketData[]>);

      const comparisons: MarketComparison[] = [];

      for (const [key, items] of Object.entries(groupedData)) {
        const [product_name, category] = key.split('-');
        
        const locations = items.map(item => ({
          location: item.location,
          price: item.price,
          quantity: item.quantity,
          demand_level: item.demand_level,
          supply_level: item.supply_level
        }));

        const prices = items.map(item => item.price);
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        const priceVariance = ((maxPrice - minPrice) / minPrice) * 100;

        const bestPriceItem = items.find(item => item.price === minPrice);

        comparisons.push({
          product_name,
          category,
          locations,
          best_price_location: bestPriceItem?.location || '',
          best_price: minPrice,
          price_variance: priceVariance,
          recommendation: this.generateRecommendation(items)
        });
      }

      return comparisons;
    } catch (error) {
      console.error('Error generating market comparison:', error);
      return [];
    }
  }

  // Get demand forecasts
  static async getDemandForecasts(
    productName?: string,
    location?: string,
    category?: string
  ): Promise<DemandForecast[]> {
    try {
      void productName;
      void location;
      void category;
      return [];
    } catch (error) {
      console.error('Error fetching demand forecasts:', error);
      return [];
    }
  }

  // Get supply chain optimization recommendations
  static async getSupplyChainRecommendations(
    productName: string,
    sourceLocation: string,
    targetLocation: string
  ): Promise<{
    optimal_route: string[];
    estimated_cost: number;
    estimated_time: number;
    risk_factors: string[];
    recommendations: string[];
  }> {
    void productName;
    void sourceLocation;
    void targetLocation;
    throw new Error('Supply chain recommendations require a backend integration.');
  }

  // Get market alerts and notifications
  static async getMarketAlerts(
    userId: string,
    preferences?: {
      products?: string[];
      locations?: string[];
      price_threshold?: number;
    }
  ): Promise<{
    id: string;
    type: 'price_spike' | 'price_drop' | 'demand_increase' | 'supply_shortage';
    product_name: string;
    location: string;
    message: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    created_at: string;
  }[]> {
    try {
      void userId;
      void preferences;
      return [];
    } catch (error) {
      console.error('Error fetching market alerts:', error);
      return [];
    }
  }

  // Generate recommendation based on market data
  private static generateRecommendation(items: MarketData[]): string {
    const avgPrice = items.reduce((sum, item) => sum + item.price, 0) / items.length;
    const priceVariance = Math.max(...items.map(item => item.price)) - Math.min(...items.map(item => item.price));
    
    if (priceVariance > avgPrice * 0.3) {
      return 'High price variance detected. Consider arbitrage opportunities.';
    } else if (items.some(item => item.demand_level === 'high' && item.supply_level === 'low')) {
      return 'High demand, low supply detected. Consider increasing production.';
    } else if (items.some(item => item.demand_level === 'low' && item.supply_level === 'high')) {
      return 'Low demand, high supply detected. Consider diversifying or reducing production.';
    } else {
      return 'Market conditions are stable. Monitor for changes.';
    }
  }

  // Subscribe to market updates
  static subscribeToMarketUpdates(
    callback: (update: MarketData) => void
  ): () => void {
    const subscription = supabase
      .channel('market_updates')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'market_data'
      }, (payload) => {
        callback(payload.new as MarketData);
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }
} 
